import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Edit2, Eye, X } from 'lucide-react';
import { useEstimationState } from '../hooks/use_estimation_state';
import { EstimationSidebar } from '../components/estimation_sidebar';
import { EstimationCatalog } from '../components/estimation_catalog';
import { EstimationItems } from '../components/estimation_items';
import { EstimationSummary } from '../components/estimation_summary';
import { API_BASE_URL } from '../../../../services/api';

export const EstimationScreen = ({ onCancel, onPlaceOrder, initialData }) => {
  const location = useLocation();
  const existingEstimation = location.state?.estimation || null;
  const customerData = location.state?.customer || initialData?.customer || (existingEstimation?.customer_id && typeof existingEstimation.customer_id === 'object' ? existingEstimation.customer_id : { _id: initialData?.leadId });
  const estimationHook = useEstimationState(customerData, existingEstimation, onCancel, onPlaceOrder);
  
  const { isLocked, setIsLocked, currentEstimationId, estimationStatus } = estimationHook.state;
  const [showPdfModal, setShowPdfModal] = useState(false);
  
  const ORDER_PLACED_STATUSES = ['ORDER_PLACED', 'ORDER_APPROVED', 'SENT_TO_STORE', 'COMPLETED', 'DISPATCHED'];
  const isOrderPlaced = ORDER_PLACED_STATUSES.includes(estimationStatus);

  return (
    <div className="flex flex-col w-full relative">
      {/* Estimation Selector Header */}
      <div className="flex items-center justify-between bg-white px-4 py-3 border-b border-slate-200 shadow-sm mb-4 rounded-lg z-10">
        <div className="flex items-center gap-4">
          <span className="text-sm font-semibold text-slate-700">Select Estimation:</span>
          <select 
            className="border border-slate-300 rounded-md text-sm px-3 py-1.5 min-w-[300px] focus:outline-none focus:border-blue-500 bg-slate-50 text-slate-700 font-medium cursor-pointer"
            value={currentEstimationId || ''}
            onChange={(e) => {
              if (e.target.value === 'new') {
                estimationHook.actions.handleNewEstimation();
              } else {
                estimationHook.actions.handleSelectEstimation(e.target.value);
              }
            }}
          >
            {estimationHook.state.estimationsList.length === 0 && <option value="" disabled>No Estimations Found</option>}
            {!currentEstimationId && estimationHook.state.estimationsList.length > 0 && <option value="" disabled>Drafting New Estimation...</option>}
            {estimationHook.state.estimationsList.map((est, idx) => {
              const estNum = estimationHook.state.estimationsList.length - idx;
              const dateStr = new Date(est.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
              return (
                <option key={est._id} value={est._id}>
                  Estimation #{estNum} — {dateStr} ({est.status.replace(/_/g, ' ')})
                </option>
              );
            })}
            <option value="new" className="font-bold text-[#C91D25]">+ Create New Estimation</option>
          </select>
        </div>
        <button
          onClick={() => estimationHook.actions.handleNewEstimation()}
          className="px-4 py-1.5 bg-[#C91D25] text-white text-sm font-semibold rounded-md hover:bg-[#a8161d] transition-colors shadow-sm"
        >
          + New Estimation
        </button>
      </div>

      {isLocked && (
        <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-lg mb-4 flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
            <span className="font-semibold text-sm">
              {isOrderPlaced ? 'Order Placed — Estimation Locked' : 'Estimation Locked (View Only)'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={() => setShowPdfModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-300 text-slate-700 rounded text-xs font-bold hover:bg-slate-100 transition-colors shadow-sm"
            >
              <Eye size={14} />
              View PDF
            </button>
            {!isOrderPlaced && (
              <button 
                onClick={() => setIsLocked(false)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-blue-300 text-blue-700 rounded text-xs font-bold hover:bg-blue-100 transition-colors shadow-sm"
              >
                <Edit2 size={14} />
                Edit Estimation
              </button>
            )}
          </div>
        </div>
      )}
      
      <div className={`flex flex-col xl:flex-row w-full h-[calc(100vh-140px)] ${isLocked ? 'opacity-80 grayscale-[20%]' : ''}`}>
        
        {/* Left Side: Product Catalog (60-70%) */}
        <div className="w-full xl:w-2/3 h-full overflow-hidden border-r border-slate-200">
          {/* Commented due to update. Uncomment this and remove EstimationCatalog to revert to sidebar navigation. */}
          {/* <EstimationSidebar state={estimationHook.state} actions={estimationHook.actions} /> */}
          <EstimationCatalog state={estimationHook.state} actions={estimationHook.actions} />
        </div>
        
        {/* Right Side: Estimation Builder (30-40%) */}
        <div className={`w-full xl:w-1/3 flex flex-col min-w-0 h-full overflow-y-auto ${isLocked ? 'pointer-events-none' : 'pointer-events-auto'}`}>
          <div className="bg-white flex flex-col h-full p-6">
            <EstimationItems state={estimationHook.state} actions={estimationHook.actions} />
            <EstimationSummary state={estimationHook.state} computed={estimationHook.computed} actions={estimationHook.actions} />
          </div>
        </div>
      </div>

      {showPdfModal && currentEstimationId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-[90vw] max-w-5xl h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50 shrink-0">
              <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <Eye size={18} className="text-slate-500" />
                Estimation Preview
              </h3>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => window.open(`${API_BASE_URL}/api/estimations/${currentEstimationId}/stream-pdf`, '_blank')}
                  className="px-3 py-1.5 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 hover:bg-blue-100 rounded-md transition-colors"
                >
                  Open in New Tab
                </button>
                <button 
                  onClick={() => setShowPdfModal(false)}
                  className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-200 rounded-md transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="flex-1 w-full bg-slate-200 p-2">
              <iframe 
                src={`${API_BASE_URL}/api/estimations/${currentEstimationId}/stream-pdf`} 
                className="w-full h-full rounded bg-white shadow-sm border border-slate-300"
                title="PDF Preview"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
